import { useState, useEffect } from 'react';
import { backdropClose } from '../utils/backdropClose';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';

interface AddPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface Branch {
  id: string;
  name: string;
  displayName: string;
  addressLine: string | null;
  city: string | null;
}

interface FormData {
  name: string;
  description: string;
  durationMonths: string;
  price: string;
  gstPercent: string;
  isDefault: boolean;
  branchIds: string[];
  features?: any;
}

// Removed defaultFeatures as we're simplifying the plan structure

export default function AddPlanModal({ isOpen, onClose, onSuccess }: AddPlanModalProps) {
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    durationMonths: '',
    price: '',
    gstPercent: '18',
    isDefault: false,
    branchIds: []
  });
  // Removed features state as we're simplifying the plan structure
  const [formErrors, setFormErrors] = useState<Partial<FormData>>({});

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        description: '',
        durationMonths: '',
        price: '',
        gstPercent: '18',
        isDefault: false,
        branchIds: []
      });
      fetchBranches();
    }
  }, [isOpen]);

  const fetchBranches = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const apiUrl = API_BASE_URL;

      console.log('🔍 Debug - Fetching all accessible branches');
      const branchesResponse = await fetch(`${apiUrl}/api/branches`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('🔍 Debug - Branches response status:', branchesResponse.status);
      if (branchesResponse.ok) {
        const branchesData = await branchesResponse.json();
        console.log('🔍 Debug - Branches data:', branchesData);
        setBranches(branchesData.data || []);
      } else {
        const errorData = await branchesResponse.json();
        console.error('🔍 Debug - Branches fetch error:', errorData);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      // Don't show error toast as this is not critical for plan creation
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (formErrors[name as keyof FormData]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Removed handleFeatureChange as we're simplifying the plan structure

  const handleBranchToggle = (branchId: string) => {
    setFormData(prev => ({
      ...prev,
      branchIds: prev.branchIds.includes(branchId)
        ? prev.branchIds.filter(id => id !== branchId)
        : [...prev.branchIds, branchId]
    }));
  };

  const validateForm = (): boolean => {
    const errors: Partial<FormData> = {};

    if (!formData.name.trim()) {
      errors.name = 'Plan name is required';
    }

    if (!formData.price) {
      errors.price = 'Price is required';
    } else if (isNaN(Number(formData.price))) {
      errors.price = 'Price must be a valid number';
    }

    if (!formData.durationMonths) {
      errors.durationMonths = 'Duration is required';
    } else if (isNaN(Number(formData.durationMonths)) || Number(formData.durationMonths) < 1) {
      errors.durationMonths = 'Duration must be a positive number';
    }

    // Removed maxBranches and sortOrder validation as we're simplifying the plan structure

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const apiUrl = API_BASE_URL;

      const payload: any = {
        name: formData.name,
        description: formData.description,
        durationMonths: Number(formData.durationMonths),
        price: Number(formData.price),
        gstPercent: formData.gstPercent === '' ? 0 : Number(formData.gstPercent),
        isDefault: formData.isDefault,
        branchIds: formData.branchIds
      };
      if (formData.features) {
        payload.features = formData.features;
      }

      console.log('🔍 Debug - Payload being sent:', payload);
      console.log('🔍 Debug - Selected branch IDs:', formData.branchIds);

      const url = `${apiUrl}/api/member-plans`;
      const method = 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Plan created successfully!');
        onSuccess();
        onClose();
        // Reset form
        setFormData({
          name: '',
          description: '',
          durationMonths: '',
          price: '',
          gstPercent: '18',
          isDefault: false,
          branchIds: []
        });
        setFormErrors({});
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to create plan');
      }
    } catch (error) {
      console.error('Error creating plan:', error);
      toast.error('Failed to create plan');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000] p-2 sm:p-4" {...backdropClose(onClose)}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">
              Create New Membership Plan
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
              Basic Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plan Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="Enter plan name"
                />
                {formErrors.name && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.price ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="Enter price"
                  min="0"
                  step="0.01"
                />
                {formErrors.price && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.price}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  GST %
                </label>
                <input
                  type="number"
                  name="gstPercent"
                  value={formData.gstPercent}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="18"
                  min="0"
                  max="100"
                  step="0.01"
                />
                <p className="text-xs text-gray-500 mt-1">Tax rate charged when a registered branch sells this plan. Use 0 for exempt.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration (Months) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="durationMonths"
                  value={formData.durationMonths}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.durationMonths ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="Enter duration"
                  min="1"
                />
                {formErrors.durationMonths && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.durationMonths}</p>
                )}
              </div>

              <div className="flex items-center">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    name="isDefault"
                    checked={formData.isDefault}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    Set as default plan
                  </span>
                </label>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter plan description"
                />
              </div>
            </div>
          </div>



          {/* Branch Assignment */}
          {branches.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                Branch Assignment
              </h3>

              <div>
                <p className="text-sm text-gray-600 mb-3">
                  Select which branches this plan will be available at. Leave empty to make it available at all branches.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {branches.map(branch => (
                    <label key={branch.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.branchIds.includes(branch.id)}
                        onChange={() => handleBranchToggle(branch.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">
                        {branch.displayName || branch.name}
                        {branch.city && ` (${branch.city})`}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading && (
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              <span>{loading ? 'Creating...' : 'Create Plan'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
